import React from "react";
import { Card, CardBody, Button } from "reactstrap";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import { apiErrorMessage } from "../../shared/api-error";
import EmptyState from "../../components/Page/EmptyState";
import "../../assets/css/garage-tiers.css";
import {
  FormModal,
  FormSection,
  FormGrid,
  TextField,
  FormAlert,
  FormActions,
} from "../../components/Form/FormKit";

/** "AED 45.00 / hour" — in the garage's own currency when it has one. */
const rateLabel = (rate, symbol) => {
  const amount = Number(rate || 0).toFixed(2);
  return `${symbol ? `${symbol} ` : ""}${amount} ${strings.per_hour}`;
};

/** Adds or edits one tier: its name and hourly rate. */
class TierForm extends React.Component {
  constructor(props) {
    super(props);
    const tier = props.item || {};
    this.state = {
      name: tier.name || "",
      hourlyRate:
        tier.hourlyRate === undefined || tier.hourlyRate === null
          ? ""
          : `${tier.hourlyRate}`,
      loading: false,
      error: "",
      errors: {},
    };
    this.engine = new RequestEngine();
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const { name, hourlyRate } = this.state;
    const rate = parseFloat(hourlyRate);
    const errors = {};
    if (!`${name}`.trim()) errors.name = strings.tier_name_required;
    if (Number.isNaN(rate) || rate < 0) errors.hourlyRate = strings.tier_rate_hint;
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ loading: true, error: "", errors: {} });
    const { item, path } = this.props;
    const payload = { name: `${name}`.trim(), hourlyRate: rate };
    try {
      const response = item
        ? await this.engine.putPath(`${path}/${item.id}`, payload)
        : await this.engine.saveItem(path, payload);
      if (response && (response.status === 200 || response.status === 201)) {
        this.props.onSaved();
        return;
      }
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
    this.setState({ loading: false });
  };

  render() {
    const { name, hourlyRate, loading, error, errors } = this.state;
    const symbol = this.props.currencySymbol;
    return (
      <form className="mform" onSubmit={this.handleSubmit}>
        <FormSection title={strings.tier} icon="nc-icon nc-badge">
          <FormGrid>
            <TextField
              label={strings.tier_name}
              required
              help={strings.tier_name_hint}
              value={name}
              onChange={(value) => this.setState({ name: value })}
              error={errors.name}
            />
            <TextField
              label={
                symbol
                  ? `${strings.hourly_rate} (${symbol})`
                  : strings.hourly_rate
              }
              type="number"
              min="0"
              step="0.01"
              required
              value={hourlyRate}
              onChange={(value) => this.setState({ hourlyRate: value })}
              error={errors.hourlyRate}
            />
          </FormGrid>
        </FormSection>
        <FormAlert>{error}</FormAlert>
        <FormActions onCancel={this.props.onCancel} loading={loading} />
      </form>
    );
  }
}

/**
 * The garage's staff tiers and what each costs an hour. Each member of staff
 * is placed on one from the staff form.
 */
export default class GarageTiersPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tiers: [],
      loading: true,
      formOpen: false,
      editing: null,
      error: "",
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.load();
  }

  get path() {
    return `merchants-branches/${this.props.branchId}/tiers`;
  }

  load = async () => {
    try {
      const response = await this.engine.getItem(this.path);
      const tiers = Array.isArray(response && response.data)
        ? response.data
        : [];
      this.setState({ tiers, loading: false });
    } catch (err) {
      this.setState({ loading: false, error: apiErrorMessage(err) });
    }
  };

  openForm = (editing = null) => this.setState({ formOpen: true, editing });
  closeForm = () => this.setState({ formOpen: false, editing: null });
  saved = () => this.setState({ formOpen: false, editing: null }, this.load);

  remove = async (tier) => {
    if (!window.confirm(strings.confirm_remove_tier)) return;
    try {
      await this.engine.deleteItem(this.path, tier.id);
      this.load();
    } catch (err) {
      this.setState({ error: apiErrorMessage(err) });
    }
  };

  render() {
    const { tiers, loading, formOpen, editing, error } = this.state;
    const { canWrite, currencySymbol, branchName } = this.props;

    return (
      <Card className="garage-tiers">
        <CardBody>
          <div className="garage-tiers__head">
            <div>
              <h5 className="garage-tiers__title">
                <i className="nc-icon nc-badge" /> {strings.staff_tiers}
              </h5>
              <p className="garage-tiers__hint">{strings.staff_tiers_hint}</p>
            </div>
            {canWrite && (
              <Button color="primary" onClick={() => this.openForm()}>
                <i className="nc-icon nc-simple-add" /> {strings.add_tier}
              </Button>
            )}
          </div>

          <FormAlert>{error}</FormAlert>

          {!loading && tiers.length === 0 ? (
            <EmptyState
              icon="nc-icon nc-badge"
              title={strings.no_tiers}
              text={strings.no_tiers_hint}
            />
          ) : (
            <div className="garage-tiers__list">
              {tiers.map((tier) => (
                <div className="garage-tiers__row" key={tier.id}>
                  <span className="garage-tiers__name">{tier.name}</span>
                  <span className="garage-tiers__rate">
                    {rateLabel(tier.hourlyRate, currencySymbol)}
                  </span>
                  {canWrite && (
                    <span className="garage-tiers__actions">
                      <button
                        type="button"
                        className="garage-tiers__btn"
                        onClick={() => this.openForm(tier)}
                      >
                        {strings.edit}
                      </button>
                      <button
                        type="button"
                        className="garage-tiers__btn"
                        onClick={() => this.remove(tier)}
                      >
                        {strings.delete}
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>

        <FormModal
          isOpen={formOpen}
          onClose={this.closeForm}
          icon="nc-icon nc-badge"
          title={editing ? `${strings.edit} ${strings.tier}` : strings.add_tier}
          subtitle={branchName}
        >
          <TierForm
            key={(editing && editing.id) || "new"}
            item={editing}
            path={this.path}
            currencySymbol={currencySymbol}
            onSaved={this.saved}
            onCancel={this.closeForm}
          />
        </FormModal>
      </Card>
    );
  }
}
